const getMessageEncoding = (text) => {
    const enc = new TextEncoder();
    return enc.encode(text);
};

export const generateRSAKeyPair = async () => {
    const keyPair = await window.crypto.subtle.generateKey(
        {
            name: "RSA-OAEP",
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: "SHA-256",
        },
        true,
        ["encrypt", "decrypt"]
    );
    return keyPair;
};

export const exportPublicKey = async (publicKey) => {
    const exported = await window.crypto.subtle.exportKey("spki", publicKey);
    return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

export const exportPrivateKey = async (privateKey) => {
    const exported = await window.crypto.subtle.exportKey("pkcs8", privateKey);
    return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

export const encryptMessage = async (text, publicKeyBase64) => {
    if (!publicKeyBase64 || typeof publicKeyBase64 !== 'string') {
        console.error("Error: Public Key for encryption not found!");
        return ""; 
    }

    const cleanBase64 = publicKeyBase64.replace(/\s+/g, '');

    try {
        const binaryDer = Uint8Array.from(atob(cleanBase64), c => c.charCodeAt(0));
        
        const publicKey = await window.crypto.subtle.importKey(
            "spki",
            binaryDer,
            { name: "RSA-OAEP", hash: "SHA-256" },
            true,
            ["encrypt"]
        );

        const encodedMessage = getMessageEncoding(text);
        
        const encryptedBuffer = await window.crypto.subtle.encrypt(
            { name: "RSA-OAEP" },
            publicKey,
            encodedMessage
        );

        return btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));
    } catch (error) {
        console.error("Error during encryption (encryptMessage):", error);
        throw error;
    }
};

export const decryptMessage = async (encryptedBase64, privateKeyBase64) => {
    if (!encryptedBase64 || !privateKeyBase64 || typeof privateKeyBase64 !== 'string') {
        return ""; 
    }

    const cleanEncryptedBase64 = encryptedBase64.replace(/\s+/g, '');
    const cleanPrivateKeyBase64 = privateKeyBase64.replace(/\s+/g, '');

    try {
        const keyBinaryDer = Uint8Array.from(atob(cleanPrivateKeyBase64), c => c.charCodeAt(0));
        const privateKey = await window.crypto.subtle.importKey(
            "pkcs8",
            keyBinaryDer,
            { name: "RSA-OAEP", hash: "SHA-256" },
            true,
            ["decrypt"]
        );

        const encryptedBuffer = Uint8Array.from(atob(cleanEncryptedBase64), c => c.charCodeAt(0));
        
        const decryptedBuffer = await window.crypto.subtle.decrypt(
            { name: "RSA-OAEP" },
            privateKey,
            encryptedBuffer
        );

        const dec = new TextDecoder();
        return dec.decode(decryptedBuffer); 
    } catch (error) {
        console.error("Decryption error (decryptMessage):", error);
        return "[Message cannot be decrypted]";
    }
};

const deriveKeyFromPassword = async (password, salt) => {
    const enc = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
        "raw", enc.encode(password), { name: "PBKDF2" }, false, ["deriveBits", "deriveKey"]
    );
    return window.crypto.subtle.deriveKey(
        { name: "PBKDF2", salt: salt, iterations: 100000, hash: "SHA-256" },
        keyMaterial, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]
    );
};

export const encryptPrivateKeyWithPassword = async (privateKeyBase64, password) => {
    const enc = new TextEncoder();
    const salt = window.crypto.getRandomValues(new Uint8Array(16)); 
    const iv = window.crypto.getRandomValues(new Uint8Array(12));  

    const aesKey = await deriveKeyFromPassword(password, salt);
    
    const encryptedContent = await window.crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv },
        aesKey,
        enc.encode(privateKeyBase64)
    );

    const encryptedBuffer = new Uint8Array(encryptedContent);
    const resultBuffer = new Uint8Array(salt.length + iv.length + encryptedBuffer.length);
    resultBuffer.set(salt, 0);
    resultBuffer.set(iv, salt.length);
    resultBuffer.set(encryptedBuffer, salt.length + iv.length);

    return btoa(String.fromCharCode(...resultBuffer));
};

export const decryptPrivateKeyWithPassword = async (encryptedDataB64, password) => {
    try {
        const encryptedData = Uint8Array.from(atob(encryptedDataB64), c => c.charCodeAt(0));
        
        const salt = encryptedData.slice(0, 16);
        const iv = encryptedData.slice(16, 28);
        const data = encryptedData.slice(28);

        const aesKey = await deriveKeyFromPassword(password, salt);

        const decryptedContent = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv: iv },
            aesKey,
            data
        );

        const dec = new TextDecoder();
        return dec.decode(decryptedContent);
    } catch (error) {
        console.error("Wrong password or corrupted private key data!", error);
        throw new Error("Unable to decrypt Private Key. Incorrect password?");
    }
};